import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Button, Checkbox, Typography} from 'antd';
import {InboxOutlined, UploadOutlined} from '@ant-design/icons';
import {ModelDTO} from 'hemwb-api';
import {ModelTreeNode, TreeNode, useModelFileUploader} from '../../../../../store/modelFile';
import {PropertySelection} from './types';
import {calculateFilePath, onDocumentDragOver} from '../ModelEdit/ModelEditFiles/utils';
import {calculateFileName} from '../../../../../store/modelFile/utils';
import FileTreeAntd from '../../../../core/FileTree/FileTreeAntd';
import {isModelExternal, isModelWeb} from '../../../../../store/model';

const {Text} = Typography;

type StepFilesCloneRedesignedProps = {
  model: ModelDTO;
  files: ModelTreeNode[];
  filesSelection: PropertySelection;
  toggleFile: (uuid: string) => void;
  loadNewSelection: (uuid: string[]) => void;
};

const StepFilesCloneRedesigned: React.FC<StepFilesCloneRedesignedProps> = ({
  files,
  filesSelection,
  toggleFile,
  model,
  loadNewSelection,
}) => {
  const {add, uploader} = useModelFileUploader(model, files);
  const {nodes} = uploader;
  const externalModel = isModelExternal(model);
  const webModel = isModelWeb(model);

  useEffect(() => {
    document.addEventListener('dragover', onDocumentDragOver, false);
    document.addEventListener('drop', onDocumentDragOver, false);
    return () => {
      document.removeEventListener('dragover', onDocumentDragOver);
      document.removeEventListener('drop', onDocumentDragOver, false);
    };
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const isDirUploadAvailable = useMemo(() => {
    const input = document.createElement('input');
    return typeof input.webkitdirectory === 'boolean';
  }, []);

  const dispatchAddFile = useCallback(
    (file: File, node: TreeNode | string) => {
      let path: string;
      if (typeof node === 'string') {
        path = node;
      } else {
        const nodePathName = `${node.path}/`;
        path = `${node.path === '' ? '' : nodePathName}${calculateFileName(file.name)}`;
      }
      add(file, path);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (folderInputRef.current) folderInputRef.current.value = '';
    },
    [add],
  );

  const handleAddFiles = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      if (event.currentTarget.files) {
        Array.from(event.currentTarget.files).forEach((file: File) => {
          const path = calculateFilePath(calculateFileName(file.name), file.webkitRelativePath);
          dispatchAddFile(file, path);
        });
      }
    },
    [dispatchAddFile],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent, node: TreeNode) => {
      if (!node || !node.folder || externalModel) return;

      function traverseFileTree(item: any, p?: string) {
        const path = p || '';
        if (item.isFile) {
          item.file((file: File) => {
            const newPath = `${node.path}${node.path ? '/' : ''}${path}${file.name}`;
            dispatchAddFile(file, newPath);
          });
        } else if (item.isDirectory) {
          const dirReader = item.createReader();
          dirReader.readEntries((entries: any[]) => {
            for (const entry of entries) {
              traverseFileTree(entry, path + item.name + '/');
            }
          });
        }
      }

      if (event.dataTransfer.items) {
        for (const item of event.dataTransfer.items) {
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry();
            if (entry) {
              traverseFileTree(entry);
            } else {
              const file = item.getAsFile();
              if (file) dispatchAddFile(file, node);
            }
          }
        }
      } else {
        for (const file of event.dataTransfer.files) {
          dispatchAddFile(file, node);
        }
      }
    },
    [dispatchAddFile, externalModel],
  );

  // When new files are added (not-yet-uploaded), auto-select them
  useEffect(() => {
    const newlyAdded = nodes.filter((n) => !n.folder && !n.uploaded).map((n) => n.uuid);
    if (newlyAdded.length > 0) {
      loadNewSelection(newlyAdded);
    }
  }, [nodes, loadNewSelection]);

  const renderButtons = useCallback(
    (node: TreeNode) => {
      if (node.folder) return null;
      return [
        <Checkbox key={node.uuid} checked={!!filesSelection[node.uuid]} onChange={() => toggleFile(node.uuid)} />,
      ];
    },
    [filesSelection, toggleFile],
  );

  const handleDropZoneDrop = useCallback(
    (event: React.DragEvent) => {
      handleDrop(event, {uuid: '', path: '', name: '', folder: true, uploaded: true});
    },
    [handleDrop],
  );

  return (
    <div>
      <h2 style={{fontSize: 16, fontWeight: 600, color: '#00a0a0'}}>Upload new Model file</h2>
      {/* Upload zone */}
      <div
        style={{
          border: '1px dashed #d9d9d9',
          borderRadius: '8px',
          padding: '32px',
          textAlign: 'center',
          marginBottom: '24px',
          background: '#fafafa',
          cursor: 'pointer',
        }}
        onDrop={handleDropZoneDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        <InboxOutlined style={{fontSize: '48px', color: '#006A63', marginBottom: '16px'}} />
        <Text style={{fontSize: '16px', display: 'block', marginBottom: '8px', color: '#595959'}}>
          Click or drag file to this area to upload
        </Text>
        <Text type="secondary" style={{display: 'block', marginBottom: '20px'}}>
          Support for a single or bulk upload.
        </Text>
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center'}}>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            size="large"
            style={{
              background: '#006A63',
              borderColor: '#006A63',
              height: '40px',
              width: '280px',
              fontSize: '14px',
              fontWeight: 500,
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload files from your computer
          </Button>
          {isDirUploadAvailable && (
            <Button
              type="default"
              icon={<UploadOutlined />}
              size="large"
              style={{
                height: '40px',
                width: '280px',
                fontSize: '14px',
                fontWeight: 500,
                borderColor: '#d9d9d9',
                color: '#000000',
              }}
              onClick={() => folderInputRef.current?.click()}
            >
              Upload folder from your computer
            </Button>
          )}
        </div>
      </div>

      <input type="file" multiple onChange={handleAddFiles} ref={fileInputRef} style={{display: 'none'}} />
      {isDirUploadAvailable && (
        <input
          type="file"
          {...({webkitdirectory: 'webkitdirectory'} as any)}
          onChange={handleAddFiles}
          ref={folderInputRef}
          style={{display: 'none'}}
        />
      )}

      {/* Existing file tree */}
      {nodes && nodes.length > 0 && (
        <FileTreeAntd
          renderButtons={renderButtons}
          files={nodes}
          webModel={webModel}
          externalModel={externalModel}
          cloneFlow={true}
          onDrop={handleDrop}
        />
      )}
    </div>
  );
};

export default StepFilesCloneRedesigned;
